/**
 * "Novedad": resalta un control recién liberado con un halo pulsante + un badge "novedad".
 *
 * Se muestra durante las primeras `maxApariciones` (4 por default) VECES QUE EL CONTROL APARECE en
 * pantalla —una por sesión y por clave—, y luego se retira solo. USARLO NO lo apaga: antes desaparecía
 * al primer uso y al director se le fue en un instante (2026-09-24). Tampoco es por días (podría no
 * abrirlo en 4 días): se cuenta solo cuando de verdad está frente a esa pantalla.
 *
 * Cada `clave` lleva su PROPIO conteo (localStorage, namespace `supernova:novedad:*`), así que cada
 * cosa nueva tiene sus 4 visitas independientes: ver RFI 4 veces no consume las de Procedimiento.
 * Persiste por navegador. Reutilizable para cada cosa nueva: `useNovedad('clave-nueva')`.
 *
 * Uso:
 *   const nov = useNovedad('procedimiento');
 *   const c = useNovedadStyles();
 *   <Button className={nov.visible ? c.halo : undefined} onClick={() => { nov.marcarVisto(); abrir(); }}>
 *     Procedimiento constructivo {nov.visible && <span className={c.badge}>novedad</span>}
 *   </Button>
 */
import { useCallback, useEffect, useState } from 'react';
import { makeStyles } from '@fluentui/react-components';

const PREFIX = 'supernova:novedad:';
const APARICIONES_DEFAULT = 4;

/** Conteo de apariciones ya registradas para una clave. Sin storage → tratar como "ya visto". */
function leerConteo(key: string): number {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return 0;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return Number.MAX_SAFE_INTEGER; // sin storage (modo privado, etc.) → no molestar
  }
}

export function useNovedad(
  clave: string,
  maxApariciones: number = APARICIONES_DEFAULT,
): { visible: boolean; marcarVisto: () => void } {
  const key = PREFIX + clave;
  // Se decide al montar con el conteo ANTERIOR: la aparición de esta sesión se cuenta (abajo) pero
  // no oculta el halo a mitad de sesión — se muestra completo esta vez y se retira a la siguiente.
  const [visible] = useState<boolean>(() => leerConteo(key) < maxApariciones);

  useEffect(() => {
    if (!visible) return;
    // Cuenta UNA aparición por sesión y por clave: ver la misma pantalla varias veces en una sesión
    // (o un remount) no gasta más de una. Cada clave es independiente.
    const sesKey = 'ses:' + key;
    try {
      if (sessionStorage.getItem(sesKey)) return; // ya contada esta sesión
      sessionStorage.setItem(sesKey, '1');
      const n = leerConteo(key);
      if (n < maxApariciones) localStorage.setItem(key, String(n + 1));
    } catch {
      /* sin storage: se ve igual esta vez, no se persiste */
    }
  }, [visible, key, maxApariciones]);

  // Usar el control YA NO oculta el halo (antes desaparecía al primer uso, demasiado rápido). Se
  // conserva por compatibilidad con los llamadores; la novedad se retira sola tras las apariciones.
  const marcarVisto = useCallback(() => {
    /* no-op: la novedad se retira por apariciones, no por uso */
  }, []);

  return { visible, marcarVisto };
}

export const useNovedadStyles = makeStyles({
  // Glow ÁMBAR que "respira": distintivo del acento azul de la app, visible en claro Y oscuro,
  // contenido (no muy ancho). El ámbar = "nuevo", no error.
  halo: {
    animationName: {
      '0%, 100%': { boxShadow: '0 0 4px 0 rgba(245, 166, 35, 0.55)' },
      '50%': { boxShadow: '0 0 11px 2px rgba(245, 166, 35, 0.95)' },
    },
    animationDuration: '1.6s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
    borderRadius: '8px',
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
      boxShadow: '0 0 6px 1px rgba(245, 166, 35, 0.85)',
    },
  },
  badge: {
    marginLeft: '6px',
    fontSize: '9px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    padding: '1px 5px',
    borderRadius: '6px',
    background: '#f5a623',
    color: '#1c1300',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  },
});
